import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { CartolaService } from '../../services/cartola.service';
import { BeneficiarioCriptomoneda } from '../../interfaces/beneficiario-criptomoneda.interface';
import { RecipientCriptoService } from '../../services/recipient-cripto.service';
import { BinanceDepositService } from '../../services/binance-deposit.service';
import { AddBeneficiaryComponent } from '../calculator-cryto/components/add-beneficiary/add-beneficiary.component';
import { BinanceWithdrawalFeeService, WithdrawalFeeCalculation } from '../../services/binance-withdrawal-fee.service';
import { BinanceWithdrawalService, WithdrawalHistoryItem, WithdrawalHistoryResponse, BalanceResponse } from '../../services/binance-withdrawal.service';
import { CryptoScreeningService } from '../../services/crypto-screening.service';
import { MediosCriptoService } from '../../services/medios-cripto.service';
import {
  avisoRedesPausadas,
  beneficiarioPausado,
  conRedPausada,
  esErrorRedInactiva,
  EstadoRed,
  etiquetaCortaRed,
  RedCripto,
  redDeBeneficiario,
  redPorId,
} from 'src/app/shared/utils/red-cripto';

@Component({
  selector: 'app-send-usdt',
  templateUrl: './send-usdt.component.html',
  styleUrls: ['./send-usdt.component.sass'],
})
export class SendUsdtComponent implements OnInit, OnDestroy {
  // Constantes
  readonly MIN_USDT_AMOUNT = 10;
  readonly HORARIO_OPERACION = '5:00 - 23:00';
  readonly OPERATING_HOURS_MESSAGE = 'No puedes operar en este momento. El horario de operación es de 5:00 a 23:00 (hora de Chile).';

  // Estado del componente
  loading = false;
  isConverting = false;
  isOperatingHours = true;
  errorMessage: string | null = null;
  loadingFee = false;
  feeCalculation: WithdrawalFeeCalculation | null = null;
  feeErrorMessage: string | null = null;

  // Datos del usuario
  usuarioID: number | undefined;
  user: any = null;

  // Datos de cuentas y saldos
  listaCuentas: BeneficiarioCriptomoneda[] = [];
  saldoUsdt = 0;
  montoAEnviar = 0;
  beneficiarioSeleccionado: BeneficiarioCriptomoneda | null = null;

  // Historial de retiros
  withdrawalHistory: WithdrawalHistoryItem[] = [];
  loadingHistory = false;
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  blockedBalance = 0;
  expandedCardId: number | null = null;

  // Inputs y Outputs
  @Input() seleccionable = false;
  @Output() seleccion: EventEmitter<BeneficiarioCriptomoneda> = new EventEmitter();

  // ViewChild para resetear el select
  @ViewChild('beneficiarioSelect') beneficiarioSelect!: ElementRef<HTMLSelectElement>;

  // Estado del screening de la wallet seleccionada.
  //
  // Vive aparte de `errorMessage` a proposito: ese banner se autoborra a los
  // 5-10 segundos, y un aviso de wallet bloqueada tiene que quedar a la vista
  // hasta que el cliente elija otro beneficiario.
  verificandoWallet = false;
  walletBloqueada = false;
  mensajeBloqueoWallet: string | null = null;

  // Redes cripto.
  //
  // 30/10/2026: la lista trae los beneficiarios de todas las redes y el retiro
  // viaja con la red del elegido. Tron (TRC20) sale por Binance como siempre;
  // Polygon y Solana no los paga Binance, así que Nest los deja en
  // POR_PROCESAR para que un operador los pague a mano.
  estadoRedes: EstadoRed[] = [];

  // Suscripciones y timers
  private subscriptions = new Subscription();
  private timeCheckInterval: any;
  private errorMessageTimeout: any;

  constructor(
    private store: Store<AppState>,
    private accountRecipientService: RecipientCriptoService,
    private dialog: MatDialog,
    private cartolaService: CartolaService,
    private currencyPipe: CurrencyPipe,
    private router: Router,
    private binanceDepositService: BinanceDepositService,
    private withdrawalFeeService: BinanceWithdrawalFeeService,
    private withdrawalService: BinanceWithdrawalService,
    private screeningService: CryptoScreeningService,
    private mediosCripto: MediosCriptoService,
  ) {}

  // Lifecycle hooks
  ngOnInit(): void {
    this.checkOperatingHours();
    this.setupTimeMonitoring();
    this.initUserSubscription();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.timeCheckInterval) {
      clearInterval(this.timeCheckInterval);
    }
    if (this.errorMessageTimeout) {
      clearTimeout(this.errorMessageTimeout);
    }
  }

  // Inicialización y suscripciones
  private initUserSubscription(): void {
    this.subscriptions.add(
      this.store.select('auth').subscribe(async ({ user }) => {
        this.user = user;
        this.usuarioID = user?.usuarioId;

        if (this.usuarioID) {
          await this.loadUserData(this.usuarioID);
        }
      })
    );
  }

  private async loadUserData(userId: number): Promise<void> {
    try {
      void this.cargarRedes();
      this.asignarCuentas(await this.accountRecipientService.getByUser(userId));
      await this.initData(String(userId));
      await this.loadWithdrawalHistory();
    } catch (error) {
      this.setTemporaryError('Error al cargar los datos del usuario.');
    }
  }

  /** Los beneficiarios de todas las redes; los de una red pausada se ven pero no se eligen. */
  private asignarCuentas(cuentas: BeneficiarioCriptomoneda[] | null | undefined): void {
    this.listaCuentas = cuentas || [];
  }

  /**
   * Estado de las redes. `listar()` nunca rechaza: si Nest no responde, todas
   * cuentan como activas y el que decide es Nest al pedir el retiro (422).
   */
  private async cargarRedes(): Promise<void> {
    this.estadoRedes = await this.mediosCripto.listar();
  }

  /** Franja si alguna red está pausada, con las que se pueden usar. */
  get avisoRedes(): string | null {
    return avisoRedesPausadas(this.estadoRedes);
  }

  /** Red del beneficiario elegido; sin red es TRC20, igual que en los backends. */
  get redSeleccionada(): RedCripto | null {
    return this.beneficiarioSeleccionado
      ? redPorId(redDeBeneficiario(this.beneficiarioSeleccionado))
      : null;
  }

  /** Polygon o Solana: no lo paga Binance sino un operador a mano. */
  get pagoManual(): boolean {
    const red = this.redSeleccionada;
    return !!red && !red.pagaBinance;
  }

  /** «TRC20», «Polygon», «Solana»: para distinguir beneficiarios en la lista. */
  etiquetaRed(cuenta: BeneficiarioCriptomoneda): string {
    return etiquetaCortaRed(
      redDeBeneficiario(cuenta),
      cuenta.medioCriptomoneda_descripcion,
    );
  }

  /** Un beneficiario de una red pausada se ve en la lista, pero no se puede elegir. */
  pausado(cuenta: BeneficiarioCriptomoneda): boolean {
    return beneficiarioPausado(cuenta, this.estadoRedes);
  }

  private checkOperatingHours(): void {
    const now = new Date();
    const chileTime = new Date(
      now.toLocaleString('en-US', { timeZone: 'America/Santiago' }),
    );
    const currentHour = chileTime.getHours();

    this.isOperatingHours = currentHour >= 5 && currentHour < 23;

    if (!this.isOperatingHours) {
      this.errorMessage = this.OPERATING_HOURS_MESSAGE;
    } else if (this.errorMessage === this.OPERATING_HOURS_MESSAGE) {
      this.errorMessage = null;
    }
  }

  private setupTimeMonitoring(): void {
    this.timeCheckInterval = setInterval(() => {
      this.checkOperatingHours();
    }, 60000);
  }

  // Método para mostrar error temporal
  private setTemporaryError(message: string, duration: number = 5000): void {
    // Limpiar timeout anterior si existe
    if (this.errorMessageTimeout) {
      clearTimeout(this.errorMessageTimeout);
    }

    this.errorMessage = message;

    this.errorMessageTimeout = setTimeout(() => {
      // Solo limpiar si el mensaje sigue siendo el mismo (no ha sido reemplazado por otro)
      if (this.errorMessage === message) {
        this.errorMessage = null;
      }
    }, duration);
  }

  private async initData(userId: string): Promise<void> {
    try {
      this.loading = true;

      // Obtener balance considerando retiros pendientes
      const balanceObservable = await this.withdrawalService.getBalance();
      const balance: BalanceResponse = await firstValueFrom(balanceObservable);

      // Usar saldo disponible (saldo total - retiros pendientes bloqueados)
      this.saldoUsdt = balance.availableBalance || 0;
      this.blockedBalance = balance.blockedBalance || 0;


      this.setupBalanceSubscription(userId);
      this.loading = false;
    } catch (error) {
      this.setTemporaryError('Error al obtener valores iniciales.');
      this.loading = false;
    }
  }

  /**
   * Actualiza solo el balance y el historial sin recargar toda la página
   */
  private async refreshBalanceAndHistory(userId: string): Promise<void> {
    try {
      // Obtener balance actualizado
      const balanceObservable = await this.withdrawalService.getBalance();
      const balance: BalanceResponse = await firstValueFrom(balanceObservable);

      // Actualizar saldos
      this.saldoUsdt = balance.availableBalance || 0;
      this.blockedBalance = balance.blockedBalance || 0;


      // Recargar historial (volver a página 1)
      this.currentPage = 1;
      await this.loadWithdrawalHistory();
    } catch (error) {
      this.setTemporaryError('Error al actualizar los datos. Por favor, recarga la página.');
    }
  }

  private setupBalanceSubscription(userId: string): void {
    this.subscriptions.add(
      this.binanceDepositService.getBalanceUpdates(userId).subscribe({
        next: (balance: number) => {
          this.saldoUsdt = balance || 0;
        },
        error: (error: Error) => {
          this.saldoUsdt = 0;
        },
      })
    );
  }

  // Métodos de formato
  formatUSDT(value: number): string {
    if (!value && value !== 0) return '0.00';
    const roundedValue = Math.floor(value * 100) / 100;
    return roundedValue.toFixed(2);
  }

  formatUSDTWithSymbol(value: number): string {
    if (!value && value !== 0) return '$0.00 USDT';
    const roundedValue = Math.floor(value * 100) / 100;
    return `$${roundedValue.toFixed(2)} USDT`;
  }

  // Métodos de cálculo y validación
  validateAmount(): void {
    // Redondear a 2 decimales
    this.montoAEnviar = Math.floor(this.montoAEnviar * 100) / 100;

    if (this.montoAEnviar > this.saldoUsdt) {
      this.montoAEnviar = this.saldoUsdt;
    }
  }

  setMaxAmount(): void {
    if (this.isOperatingHours) {
      this.montoAEnviar = this.saldoUsdt;
      // Validar y calcular fee después de establecer el monto máximo
      this.validateAmount();
    }
  }

  isValidAmount(): boolean {
    return (
      this.montoAEnviar >= this.MIN_USDT_AMOUNT &&
      this.montoAEnviar <= this.saldoUsdt &&
      this.isOperatingHours
    );
  }

  onBeneficiarioChange(event: any): void {
    const beneficiarioId = event.target.value;
    if (beneficiarioId) {
      const elegido = this.listaCuentas.find(
        (cuenta) => cuenta.beneficiario_id === Number(beneficiarioId)
      ) || null;
      // La opción de un beneficiario pausado ya viene deshabilitada; esto
      // cubre el caso en que la red se pausó con la opción ya elegida.
      this.beneficiarioSeleccionado = elegido && !this.pausado(elegido) ? elegido : null;
    } else {
      this.beneficiarioSeleccionado = null;
    }
    this.verificarWalletSeleccionada();
  }

  /**
   * Prevalida la wallet elegida para poder apagar el boton antes de que el
   * cliente escriba el monto, en vez de dejarlo completar todo y rebotarlo.
   *
   * El bloqueo de verdad lo hace el backend al pedir el retiro: si esta
   * consulta falla, el boton queda habilitado y el backend decide.
   */
  private async verificarWalletSeleccionada(): Promise<void> {
    this.walletBloqueada = false;
    this.mensajeBloqueoWallet = null;

    const direccion =
      this.beneficiarioSeleccionado?.beneficiario_medio_recepcion;
    if (!direccion) {
      return;
    }

    this.verificandoWallet = true;
    try {
      // Con el código de la red del beneficiario (TRC20, POLYGON, SOL): es el
      // `network` que entiende el screening.
      const resultado = await this.screeningService.verificar(
        direccion,
        this.redSeleccionada?.codigo ?? 'TRC20',
        'ENVIO',
      );
      if (!resultado.permitido && !resultado.requiereRevision) {
        this.walletBloqueada = true;
        this.mensajeBloqueoWallet =
          resultado.mensaje ||
          'No podemos procesar envios a esta direccion. Escribenos a soporte.';
      }
    } finally {
      this.verificandoWallet = false;
    }
  }

  canSend(): boolean {
    return (
      this.isValidAmount() &&
      this.beneficiarioSeleccionado !== null &&
      !this.pausado(this.beneficiarioSeleccionado) &&
      !this.isConverting &&
      !this.verificandoWallet &&
      !this.walletBloqueada
    );
  }

  // Métodos de interacción
  async openCreateDialog(cuenta?: BeneficiarioCriptomoneda): Promise<void> {
    const isMobile = window.innerWidth < 768;
    // Todas las redes (30/10/2026): un beneficiario de Polygon o Solana también
    // sirve acá; ese retiro lo paga un operador a mano.
    const dialogData = {
      data: {
        userID: this.usuarioID,
        ...(cuenta && { type: 'UPDATE', beneficiario: cuenta }),
      },
      disableClose: true,
      panelClass: 'aguacate-dialog',
      maxWidth: isMobile ? '95vw' : '560px',
      minWidth: isMobile ? '90vw' : '500px',
      maxHeight: '90vh',
    };

    const dialogRef = this.dialog.open(AddBeneficiaryComponent, dialogData);

    dialogRef.afterClosed().subscribe(async (result) => {
      // El diálogo pudo enterarse de que una red se pausó (422).
      void this.cargarRedes();
      if (result === true && this.usuarioID) {
        await this.reloadAccounts();
      }
    });
  }

  private async reloadAccounts(): Promise<void> {
    if (this.usuarioID) {
      this.asignarCuentas(
        await this.accountRecipientService.getByUser(this.usuarioID)
      );
    }
  }

  onSeleccion(cuenta: BeneficiarioCriptomoneda): void {
    this.seleccion.emit(cuenta);
  }

  navigateBack(): void {
    this.router.navigate(['/wallet/client/load-pays-usdt']);
  }

  // Método para calcular el fee de retiro
  async calculateFee(): Promise<void> {
    if (!this.beneficiarioSeleccionado || this.montoAEnviar < this.MIN_USDT_AMOUNT) {
      this.feeCalculation = null;
      this.feeErrorMessage = null;
      return;
    }

    try {
      this.loadingFee = true;
      this.feeErrorMessage = null;

      const observable = await this.withdrawalFeeService.calculateWithdrawalFee(
        this.beneficiarioSeleccionado.beneficiario_medio_recepcion,
        this.montoAEnviar
      );

      const response = await firstValueFrom(observable);

      if (response.success && response.data) {
        this.feeCalculation = response.data;
        this.feeErrorMessage = null; // Limpiar error si el cálculo fue exitoso
      } else {
        this.feeCalculation = null;
        this.feeErrorMessage = response.message || 'Error al calcular el fee de retiro';
      }
    } catch (error: any) {
      this.feeCalculation = null;
      this.feeErrorMessage = error.error?.message || 'Error al calcular el fee de retiro';
    } finally {
      this.loadingFee = false;
    }
  }

  // Método para cargar el historial de retiros
  async loadWithdrawalHistory(): Promise<void> {
    try {
      this.loadingHistory = true;

      const observable = await this.withdrawalService.getWithdrawalHistory(
        this.currentPage,
        this.pageSize
      );

      const response: WithdrawalHistoryResponse = await firstValueFrom(observable);

      this.withdrawalHistory = response.items;
      this.totalItems = Number(response.total) || 0;
      this.totalPages = Number(response.totalPages) || 0;
      this.currentPage = Number(response.page) || 1;
    } catch (error: any) {
      this.withdrawalHistory = [];
    } finally {
      this.loadingHistory = false;
    }
  }

  // Método para cambiar de página
  async onPageChange(page: number): Promise<void> {
    if (page < 1 || page > this.totalPages || this.totalPages === 0) {
      return;
    }
    this.currentPage = page;
    await this.loadWithdrawalHistory();
  }

  toggleCard(index: number): void {
    this.expandedCardId = this.expandedCardId === index ? null : index;
  }

  getStatusBg(status: string): string {
    switch (status) {
      case 'completed': return '#16a34a';
      case 'pending': return '#eab308';
      case 'processing': return '#f97316';
      case 'rejected': return '#dc2626';
      case 'cancelled': return '#6b7280';
      default: return '#f3f4f6';
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'completed':
      case 'pending':
      case 'processing':
      case 'rejected':
      case 'cancelled':
        return '#ffffff';
      default:
        return '#6b7280';
    }
  }

  // Método para obtener el texto del estado en español
  getStatusText(status: string): string {
    switch (status) {
      case 'completed':
        return 'Completado';
      case 'pending':
        return 'Pendiente';
      case 'processing':
        return 'En Proceso';
      case 'rejected':
        return 'Rechazado';
      case 'cancelled':
        return 'Cancelado';
      default:
        return status;
    }
  }

  // Método para enviar retiro de USDT
  async sendWithdrawal(): Promise<void> {
    if (!this.canSend() || !this.beneficiarioSeleccionado) {
      return;
    }

    // La red viaja con el retiro: Nest decide si sale por Binance (TRC20) o
    // queda para pago manual (Polygon, Solana).
    const red = this.redSeleccionada;
    const idRed = redDeBeneficiario(this.beneficiarioSeleccionado);

    try {
      this.isConverting = true;
      this.errorMessage = null;

      // Se envía fee: 0 porque la comisión de la red la cubre Aguacate
      const withdrawalRequest = {
        userId: String(this.usuarioID),
        walletAddress: this.beneficiarioSeleccionado.beneficiario_medio_recepcion,
        network: red?.codigo ?? 'TRC20',
        amount: this.montoAEnviar,
        fee: 0,
      };

      const observable = await this.withdrawalService.requestWithdrawal(withdrawalRequest);
      const response = await firstValueFrom(observable);

      if (response && response.withdrawalId) {
        // Retiro solicitado exitosamente
        this.setTemporaryError(
          red && !red.pagaBinance
            ? `✅ Retiro solicitado. ID: ${response.withdrawalId}. Lo paga un operador a mano por ${red.etiqueta}; te avisamos por correo cuando se complete.`
            : `✅ Retiro solicitado exitosamente. ID: ${response.withdrawalId}. Tu solicitud está en proceso de revisión.`,
        );

        // Limpiar formulario
        this.montoAEnviar = 0;
        this.beneficiarioSeleccionado = null;
        this.feeCalculation = null;
        this.feeErrorMessage = null;
        this.walletBloqueada = false;
        this.mensajeBloqueoWallet = null;

        // Resetear el select de beneficiario en el HTML
        if (this.beneficiarioSelect) {
          this.beneficiarioSelect.nativeElement.value = '';
        }

        // Actualizar solo balance e historial (sin recargar toda la página)
        if (this.usuarioID) {
          await this.refreshBalanceAndHistory(String(this.usuarioID));
        }
      } else {
        this.setTemporaryError('Error al procesar el retiro. Intenta nuevamente.');
      }
    } catch (error: any) {
      if (esErrorRedInactiva(error)) {
        // 422 `RED_CRIPTO_INACTIVA`: la red del beneficiario se pausó con la
        // pantalla abierta. No sale nada, así que en vez del banner que se
        // autoborra queda la franja fija de red pausada y el botón apagado. La
        // próxima lectura va a Nest en vez de a la caché.
        this.mediosCripto.invalidar();
        this.estadoRedes = conRedPausada(this.estadoRedes, idRed);
        return;
      }

      let errorMsg = '';
      const body = error?.error;

      // 1. Error es string puro (interceptor transformó el error)
      if (typeof error === 'string') {
        errorMsg = error;
      }
      // 2. Extraer del body parseado (HttpErrorResponse.error)
      else if (body && typeof body === 'object' && body.message) {
        errorMsg = body.message;
        if (body.formato) {
          errorMsg += ` (Formato: ${body.formato})`;
        }
      }
      // 3. Body como string JSON sin parsear
      else if (typeof body === 'string') {
        try {
          const parsed = JSON.parse(body);
          errorMsg = parsed.message || parsed.error || body;
          if (parsed.formato) errorMsg += ` (Formato: ${parsed.formato})`;
        } catch {
          errorMsg = body;
        }
      }
      // 4. Error con message (no técnico)
      else if (error?.message && !error.message.includes('Http failure')) {
        errorMsg = error.message;
      }

      // 5. Fallback genérico solo si no se extrajo nada útil
      if (!errorMsg) {
        errorMsg = 'Error de conexión. Por favor, verifica tu conexión e intenta nuevamente.';
      }

      // Un bloqueo por screening llega como 403 con codigo de referencia. Va
      // al aviso fijo, no al banner que se autoborra: el cliente necesita
      // poder leer el codigo y pasarselo a soporte.
      if (error?.status === 403 && body?.referencia) {
        this.walletBloqueada = true;
        this.mensajeBloqueoWallet = errorMsg;
        return;
      }

      this.setTemporaryError(`❌ ${errorMsg}`, 10000);
    } finally {
      this.isConverting = false;
    }
  }
}
